import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { 
  Calendar, 
  Plus, 
  Trash2, 
  Edit, 
  PartyPopper, 
  Music, 
  Users, 
  Trophy,
  Sparkles,
  Sun,
  Loader2,
  TrendingUp,
  TrendingDown,
  Database,
  MapPin,
  Building2,
  Palmtree
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const eventTypes = {
  festival: { label: "Festival", icon: PartyPopper, color: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400" },
  concert: { label: "Concierto", icon: Music, color: "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400" },
  conference: { label: "Conferencia", icon: Users, color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" },
  sport: { label: "Deporte", icon: Trophy, color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" },
  exhibition: { label: "Exposición", icon: Sparkles, color: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400" },
  holiday: { label: "Festivo", icon: Sun, color: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400" },
  other: { label: "Otro", icon: Calendar, color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400" }
};

const impactColors = {
  high: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  medium: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  low: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
};

const sourceInfo = {
  seed: { label: "Sitges", icon: Palmtree, color: "text-blue-600" },
  manual: { label: "Sitges", icon: Palmtree, color: "text-blue-600" },
  barcelona: { label: "Barcelona", icon: Building2, color: "text-red-600" },
  tarragona: { label: "Tarragona", icon: MapPin, color: "text-amber-600" },
  corporate: { label: "Corporativo", icon: Users, color: "text-purple-600" }
};

const LocalEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [editingEvent, setEditingEvent] = useState(null);
  const [seedingEvents, setSeedingEvents] = useState(false);
  
  const [form, setForm] = useState({
    name: "",
    description: "",
    start_date: "",
    end_date: "",
    event_type: "festival",
    impact: "medium",
    price_multiplier: 1.0
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/events`, {
        headers: getAuthHeaders()
      });
      setEvents(res.data.events || []);
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Error al cargar eventos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await axios.put(`${API}/api/events/${editingEvent.id}`, form, {
          headers: getAuthHeaders()
        });
        toast.success("Evento actualizado");
      } else {
        await axios.post(`${API}/api/events`, form, {
          headers: getAuthHeaders()
        });
        toast.success("Evento creado");
      }
      
      setIsDialogOpen(false);
      setEditingEvent(null);
      resetForm();
      fetchEvents();
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error("Error al guardar evento");
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("¿Eliminar este evento?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Evento eliminado");
      fetchEvents();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setForm({
      name: event.name,
      description: event.description || "",
      start_date: event.start_date,
      end_date: event.end_date,
      event_type: event.event_type,
      impact: event.impact,
      price_multiplier: event.price_multiplier
    });
    setIsDialogOpen(true);
  };

  const handleSeedEvents = async () => {
    setSeedingEvents(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/api/events/seed-sitges`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchEvents();
    } catch (error) {
      toast.error("Error al cargar eventos");
    } finally {
      setSeedingEvents(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      description: "",
      start_date: "",
      end_date: "",
      event_type: "festival",
      impact: "medium",
      price_multiplier: 1.0
    });
  };

  const openNewDialog = () => {
    setEditingEvent(null);
    resetForm();
    setIsDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
  };

  const getMultiplierBadge = (multiplier) => {
    const pct = Math.round((multiplier - 1) * 100);
    if (pct > 0) {
      return (
        <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 flex items-center gap-1">
          <TrendingUp className="w-3 h-3" />
          +{pct}%
        </Badge>
      );
    } else if (pct < 0) {
      return (
        <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 flex items-center gap-1">
          <TrendingDown className="w-3 h-3" />
          {pct}%
        </Badge>
      );
    }
    return <Badge variant="outline">Sin cambio</Badge>;
  };

  // Group events by upcoming/past and source
  const today = new Date().toISOString().split("T")[0];
  const upcomingEvents = events.filter(e => e.end_date >= today);
  const pastEvents = events.filter(e => e.end_date < today);
  
  // Filter by source
  const sitgesEvents = events.filter(e => !e.source || e.source === "seed" || e.source === "manual");
  const barcelonaEvents = events.filter(e => e.source === "barcelona");
  const tarragonaEvents = events.filter(e => e.source === "tarragona");
  const corporateEvents = events.filter(e => e.source === "corporate");
  
  const getFilteredUpcoming = (source) => {
    if (source === "all") return upcomingEvents;
    if (source === "sitges") return upcomingEvents.filter(e => !e.source || e.source === "seed" || e.source === "manual");
    if (source === "corporate") return upcomingEvents.filter(e => e.source === "corporate");
    return upcomingEvents.filter(e => e.source === source);
  };

  const getSourceBadge = (source) => {
    const info = sourceInfo[source] || sourceInfo.manual;
    const Icon = info.icon;
    return (
      <span className={`flex items-center gap-1 text-xs ${info.color}`}>
        <Icon className="w-3 h-3" />
        {info.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="local-events-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Eventos y Calendario</h1>
          <p className="text-muted-foreground">Eventos de Sitges, Barcelona y alrededores que afectan la demanda</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNewDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Evento
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingEvent ? "Editar Evento" : "Nuevo Evento"}</DialogTitle>
                <DialogDescription>
                  Los eventos afectan automáticamente los precios según su impacto
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre del Evento</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    placeholder="Festival de Cine de Sitges"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Descripción (opcional)</Label>
                  <Input
                    id="description"
                    value={form.description}
                    onChange={(e) => setForm({...form, description: e.target.value})}
                    placeholder="Descripción del evento..."
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Fecha Inicio</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={form.start_date}
                      onChange={(e) => setForm({...form, start_date: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">Fecha Fin</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={form.end_date}
                      onChange={(e) => setForm({...form, end_date: e.target.value})}
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Evento</Label>
                    <Select value={form.event_type} onValueChange={(v) => setForm({...form, event_type: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(eventTypes).map(([key, val]) => (
                          <SelectItem key={key} value={key}>{val.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Impacto en Demanda</Label>
                    <Select value={form.impact} onValueChange={(v) => setForm({...form, impact: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">Alto</SelectItem>
                        <SelectItem value="medium">Medio</SelectItem>
                        <SelectItem value="low">Bajo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="multiplier">Multiplicador de Precio: {form.price_multiplier}x ({Math.round((form.price_multiplier - 1) * 100)}%)</Label>
                  <input
                    type="range"
                    min="0.7"
                    max="1.5"
                    step="0.05"
                    value={form.price_multiplier}
                    onChange={(e) => setForm({...form, price_multiplier: parseFloat(e.target.value)})}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>-30%</span>
                    <span>0%</span>
                    <span>+50%</span>
                  </div>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1">
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1">
                    {editingEvent ? "Actualizar" : "Crear Evento"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold">{events.length}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">{sitgesEvents.length}</p>
              <p className="text-sm text-muted-foreground">Sitges</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600">{barcelonaEvents.length}</p>
              <p className="text-sm text-muted-foreground">Barcelona</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">{corporateEvents.length}</p>
              <p className="text-sm text-muted-foreground">Corporativos</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-600">{tarragonaEvents.length}</p>
              <p className="text-sm text-muted-foreground">Tarragona</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-rose-600">{events.filter(e => e.impact === "high").length}</p>
              <p className="text-sm text-muted-foreground">Alto Impacto</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">
                {events.filter(e => e.start_date <= today && e.end_date >= today).length}
              </p>
              <p className="text-sm text-muted-foreground">Activos Hoy</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs por zona */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="all" className="flex items-center gap-1 text-xs sm:text-sm">
            <Calendar className="w-4 h-4" />
            <span className="hidden sm:inline">Todos</span> ({upcomingEvents.length})
          </TabsTrigger>
          <TabsTrigger value="sitges" className="flex items-center gap-1 text-xs sm:text-sm">
            <Palmtree className="w-4 h-4" />
            <span className="hidden sm:inline">Sitges</span>
          </TabsTrigger>
          <TabsTrigger value="barcelona" className="flex items-center gap-1 text-xs sm:text-sm">
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">Barcelona</span>
          </TabsTrigger>
          <TabsTrigger value="corporate" className="flex items-center gap-1 text-xs sm:text-sm">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Corporativos</span>
          </TabsTrigger>
          <TabsTrigger value="tarragona" className="flex items-center gap-1 text-xs sm:text-sm">
            <MapPin className="w-4 h-4" />
            <span className="hidden sm:inline">Tarragona</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {/* Upcoming Events */}
          {getFilteredUpcoming(activeTab).length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  Próximos Eventos ({getFilteredUpcoming(activeTab).length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {getFilteredUpcoming(activeTab).map((event) => {
                    const TypeIcon = eventTypes[event.event_type]?.icon || Calendar;
                    const isActive = event.start_date <= today && event.end_date >= today;
                    
                    return (
                      <div 
                        key={event.id} 
                        className={`p-4 rounded-lg border ${isActive ? "border-green-400 bg-green-50/50 dark:bg-green-900/10" : ""}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${eventTypes[event.event_type]?.color || "bg-gray-100"}`}>
                              <TypeIcon className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold">{event.name}</h3>
                                {isActive && <Badge className="bg-green-500 text-white">EN CURSO</Badge>}
                                {getSourceBadge(event.source)}
                              </div>
                              <p className="text-sm text-muted-foreground">{event.description}</p>
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                <Badge variant="outline">{formatDate(event.start_date)} - {formatDate(event.end_date)}</Badge>
                                <Badge className={impactColors[event.impact]}>Impacto: {event.impact}</Badge>
                                {getMultiplierBadge(event.price_multiplier)}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="icon" variant="ghost" onClick={() => handleEdit(event)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDelete(event.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-muted/50">
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Sin eventos próximos</h3>
                <p className="text-muted-foreground">No hay eventos programados en esta zona</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Past Events */}
      {pastEvents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-muted-foreground">Eventos Pasados ({pastEvents.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pastEvents.slice(0, 5).map((event) => (
                <div key={event.id} className="flex items-center justify-between p-2 rounded border opacity-60">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{event.name}</span>
                    {getSourceBadge(event.source)}
                    <Badge variant="outline" className="text-xs">{formatDate(event.end_date)}</Badge>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => handleDelete(event.id)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {events.length === 0 && (
        <Card className="bg-muted/50">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Sin Eventos</h3>
            <p className="text-muted-foreground mb-4">
              Añade eventos locales de Sitges para ajustar automáticamente los precios
            </p>
            <Button onClick={handleSeedEvents}>
              <Database className="w-4 h-4 mr-2" />
              Cargar Eventos Típicos de Sitges
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LocalEvents;
